import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Select, Modal, InputNumber, Button, Input, message } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, LeftOutlined, RightOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import { useFetchWrapper } from '_helpers';
import { useCapabilities } from '_helpers/capabilities';
import { useOneOnOneActions } from '_actions/oneonone.actions';
import { useAttendanceActions } from '_actions/attendance.actions';
import dayjs from '../../_helpers/datetime';
import { classSlug } from '../../_helpers/normalize';
import { WEEKDAYS, currentIsoWeekdayVN } from '../../_helpers/schedule';

export { WeekCalendar };

const PX_PER_MIN = 1.16;           // grid vertical scale
const HEADER_H = 58;               // day-column header (weekday label + date)
const DEFAULT_RANGE = [420, 1320]; // 07:00–22:00 when no slots
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const TEACHER_PALETTES = [
    { bg: '#E9F0E2', edge: '#789263' },
    { bg: '#F3E5DB', edge: '#B47A55' },
    { bg: '#E5EEF7', edge: '#6E91A7' },
    { bg: '#EDE8F2', edge: '#9472A7' },
    { bg: '#E7F0EC', edge: '#6F9988' },
    { bg: '#F1EDEA', edge: '#A88474' },
];

const toMin = (hhmm) => {
    const m = TIME_RE.exec(hhmm || '');
    return m ? Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3)) : null;
};
const fmtMin = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

// Assign overlapping blocks (same day, sorted by start) to side-by-side lanes.
// Mutates each block with .col (lane index) and .cols (lanes in its overlap cluster).
function packColumns(blocks) {
    let cluster = [], columns = [], clusterEnd = -Infinity;
    const flush = () => {
        for (const b of cluster) b.cols = columns.length;
        cluster = []; columns = [];
    };
    for (const b of blocks) {
        if (b.start >= clusterEnd) flush();        // gap → previous cluster done
        let lane = columns.findIndex(end => b.start >= end);
        if (lane === -1) { lane = columns.length; columns.push(0); }
        columns[lane] = b.end;
        b.col = lane;
        cluster.push(b);
        clusterEnd = Math.max(clusterEnd, b.end);
    }
    flush();
}

function buildDayLayout(blocks) {
    const laidOut = [];
    let i = 0;
    while (i < blocks.length) {
        const cluster = [blocks[i]];
        let clusterEnd = blocks[i].end;
        i++;
        while (i < blocks.length && blocks[i].start < clusterEnd) {
            cluster.push(blocks[i]);
            clusterEnd = Math.max(clusterEnd, blocks[i].end);
            i++;
        }
        packColumns(cluster);
        const lanes = Math.max(...cluster.map(b => b.cols || 1));
        if (lanes > 2) {
            laidOut.push({
                cluster: true,
                items: cluster,
                start: cluster[0].start,
                end: clusterEnd,
                lanes,
            });
        } else {
            laidOut.push(...cluster);
        }
    }
    return laidOut;
}

// Stable calm color from a teacher id string.
function teacherPalette(id) {
    let h = 0;
    for (const ch of String(id || '')) h = (h * 31 + ch.charCodeAt(0)) % 360;
    return TEACHER_PALETTES[h % TEACHER_PALETTES.length];
}

// Hover-title text per block type.
const blockTitle = (blk) =>
    blk.oneOnOne ? `1:1 • ${blk.oneOnOne.student_ref?.name || ''} • ${blk.oneOnOne.teacher_ref?.name || 'chưa phân công'}`
    : blk.real ? `${blk.cls.class_name} • ${blk.real.lesson_name} • ${blk.cls.class_teacher?.name || ''}`
    : `Lịch mẫu • ${blk.cls.class_name} • ${blk.cls.class_teacher?.name || ''}`;

// Background + border per block type (solid = pending real, dashed faded = template/resolved).
function blockPalette(blk) {
    if (blk.oneOnOne) return {
        '--cal-event-bg': '#D3E5EF',
        '--cal-event-edge': 'var(--accent)',
    };
    const teacher = teacherPalette(blk.cls?.class_teacher?._id);
    if (blk.real) {
        const s = blk.real;
        return {
            '--cal-event-bg': s.cancelled ? '#FFE2DD' : teacher.bg,
            '--cal-event-edge': s.missed || s.cancelled ? 'var(--bad)' : teacher.edge,
            ...(s.held || s.cancelled
                ? { opacity: 0.72, boxShadow: 'none' }
                : {}),
        };
    }
    return {
        '--cal-event-bg': teacher.bg,
        '--cal-event-edge': teacher.edge,
        opacity: 0.62,
        boxShadow: 'none',
    };
}

function blockTime(blk) {
    if (blk.oneOnOne) return `${blk.oneOnOne.time_in}${blk.oneOnOne.time_out ? `–${blk.oneOnOne.time_out}` : ''}`;
    return `${fmtMin(blk.start)}–${fmtMin(blk.end)}`;
}

function blockName(blk) {
    if (blk.oneOnOne) return `1:1 · ${blk.oneOnOne.student_ref?.name || blk.oneOnOne.student_ref?.student_id || ''}`;
    return blk.cls?.class_name || '';
}

function blockDetail(blk) {
    if (blk.oneOnOne) return blk.oneOnOne.teacher_ref?.name || 'Chưa phân công';
    if (blk.real) return blk.real.lesson_name || blk.cls?.class_teacher?.name || '';
    return blk.cls?.class_teacher?.name || 'Lịch mẫu';
}

function StatusBadge({ blk }) {
    if (!blk.real) return null;
    const s = blk.real;
    if (s.cancelled) return <span className="cal-badge cal-cancel">Đã hủy</span>;
    if (s.held) return <span className="cal-badge cal-held">Đã học</span>;
    if (s.missed) return <span className="cal-badge cal-miss">Bỏ lỡ</span>;
    return null;
}

function EventBlock({ blk, rangeStart, openBlock }) {
    const { start, end, col, cols } = blk;
    const style = {
        top: (start - rangeStart) * PX_PER_MIN + 3,
        height: Math.max((end - start) * PX_PER_MIN - 6, 26),
        left: `calc(${(col / cols) * 100}% + 5px)`,
        width: `calc(${(1 / cols) * 100}% - 10px)`,
        ...blockPalette(blk),
    };
    const className = [
        'cal-blk',
        blk.oneOnOne ? 'cal-one' : '',
        blk.real ? 'cal-real' : 'cal-template',
        blk.real?.cancelled ? 'cal-cancelled-block' : '',
        blk.real?.missed && !blk.real?.held ? 'cal-missed-block' : '',
    ].filter(Boolean).join(' ');
    return (
        <button
            type="button"
            className={className}
            title={blockTitle(blk)}
            style={style}
            onClick={(e) => { e.stopPropagation(); openBlock(blk); }}
        >
            <span className="cal-blk-title">{blockName(blk)}</span>
            <span className="cal-blk-meta">{blockTime(blk)} · {blockDetail(blk)}</span>
            <StatusBadge blk={blk} />
        </button>
    );
}

function ClusterBlock({ blk, rangeStart, onOpen }) {
    const time = `${fmtMin(blk.start)}–${fmtMin(blk.end)}`;
    return (
        <button
            type="button"
            className="cal-cluster"
            style={{
                top: (blk.start - rangeStart) * PX_PER_MIN + 3,
                height: Math.max((blk.end - blk.start) * PX_PER_MIN - 6, 58),
            }}
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            title={`${blk.items.length} buổi cùng khung giờ`}
        >
            <span className="cal-cluster-kicker">{time}</span>
            <span className="cal-cluster-title">{blk.items.length} buổi cùng khung giờ</span>
            <span className="cal-cluster-list">
                {blk.items.slice(0, 3).map(blockName).join(' · ')}
                {blk.items.length > 3 ? ` · +${blk.items.length - 3}` : ''}
            </span>
        </button>
    );
}

function AgendaItem({ blk, openBlock }) {
    return (
        <button type="button" className="cal-agenda-item" style={blockPalette(blk)} onClick={() => openBlock(blk)}>
            <span className="cal-agenda-time">{blockTime(blk)}</span>
            <span className="cal-agenda-main">
                <span className="cal-agenda-title">{blockName(blk)}</span>
                <span className="cal-agenda-detail">{blockDetail(blk)}</span>
            </span>
            <StatusBadge blk={blk} />
        </button>
    );
}

const blankAdd = { open: false, classId: null, weekday: 1, start: '18:00', duration: 90, mode: 'weekly', lessonName: '', description: '' };
const blankEdit = { open: false, cls: null, slotIdx: -1, weekday: 1, start: '18:00', duration: 90 };
const blankSession = { open: false, s: null, cls: null };

function WeekCalendar() {
    const fetchWrapper = useFetchWrapper();
    const oneOnOneActions = useOneOnOneActions();
    const attendanceActions = useAttendanceActions();
    const history = useHistory();
    const location = useLocation();
    const caps = useCapabilities();
    const canManageSchedule = !!caps.canManageSchedule;
    const readOnly = !canManageSchedule;
    const [classes, setClasses] = useState([]);
    const [oneOnOnes, setOneOnOnes] = useState([]); // 1:1 sessions in the current ISO week
    const [realSessions, setRealSessions] = useState([]); // actual projected sessions this week
    const [teacherFilter, setTeacherFilter] = useState(null);
    const [classFilter, setClassFilter] = useState(null);
    const [add, setAdd] = useState(blankAdd);
    const [edit, setEdit] = useState(blankEdit);
    const [session, setSession] = useState(blankSession); // real session clicked → cancel/uncancel modal
    const [cancelReason, setCancelReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, ±n = weeks away
    const [weekPlan, setWeekPlan] = useState(null); // { toCreate, toSkip } preview, or null = modal closed
    const [weekPlanLoading, setWeekPlanLoading] = useState(false);
    const [focusIso, setFocusIso] = useState(currentIsoWeekdayVN());

    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const on = () => setIsMobile(mq.matches);
        mq.addEventListener('change', on);
        return () => mq.removeEventListener('change', on);
    }, []);

    const load = async () => {
        const res = await fetchWrapper.get('/api/manager/classes', null, null);
        if (!res?.ok) return;
        let body;
        try { body = await res.json(); } catch { return; }
        if (Array.isArray(body?.message)) setClasses(body.message);
    };

    // 1:1 makeup sessions falling inside the given Mon–Sun, with a usable start time.
    const loadOneOnOne = async (mon) => {
        const sunday = mon.add(6, 'day').endOf('day');
        const data = await oneOnOneActions.getAll(mon.toISOString(), sunday.toISOString());
        const recs = Array.isArray(data?.records) ? data.records : [];
        setOneOnOnes(recs.filter(r => {
            if (!r.date || !TIME_RE.test(r.time_in || '')) return false;
            const d = dayjs(r.date);
            return !d.isBefore(mon) && !d.isAfter(sunday);
        }));
    };

    // Actual projected sessions for the given Mon–Sun (the real, rolled calendar) — overlaid on the template.
    const loadReal = async (mon) => {
        const sunday = mon.add(7, 'day').startOf('day');
        const res = await fetchWrapper.get(
            `/api/manager/sessions?from=${mon.toISOString()}&to=${sunday.toISOString()}`, null, null);
        if (!res?.ok) return;
        let body;
        try { body = await res.json(); } catch { return; }
        if (Array.isArray(body?.message)) setRealSessions(body.message);
    };

    // Monday of the displayed week (weekOffset weeks from the current week).
    const monday = useMemo(
        () => dayjs().subtract(currentIsoWeekdayVN() - 1, 'day').startOf('day').add(weekOffset * 7, 'day'),
        [weekOffset]
    );
    const isToday = (iso) => monday.add(iso - 1, 'day').isSame(dayjs(), 'day');

    // "Tạo lại lịch" — bulk-fill the currently DISPLAYED week from schedule_slots, across every
    // class, ignoring lesson_plan. Preview first, confirm creates only what's still missing.
    async function openWeekPlan() {
        setWeekPlanLoading(true);
        try {
            const from = monday.toISOString();
            const to = monday.add(7, 'day').startOf('day').toISOString();
            const res = await attendanceActions.regenerateWeek(from, to, false, classFilter);
            if (res?.status === 'Success') setWeekPlan(res.message);
            else message.error(typeof res?.message === 'string' ? res.message : 'Có lỗi xảy ra');
        } finally { setWeekPlanLoading(false); }
    }

    async function confirmWeekPlan() {
        setWeekPlanLoading(true);
        try {
            const from = monday.toISOString();
            const to = monday.add(7, 'day').startOf('day').toISOString();
            const res = await attendanceActions.regenerateWeek(from, to, true, classFilter);
            if (res?.status === 'Success') {
                message.success(`Đã tạo ${res.message.created} buổi`);
                setWeekPlan(null);
                await loadReal(monday);
            } else message.error(typeof res?.message === 'string' ? res.message : 'Có lỗi xảy ra');
        } finally { setWeekPlanLoading(false); }
    }

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadOneOnOne(monday); loadReal(monday); }, [weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const q = new URLSearchParams(location.search);
        setClassFilter(q.get('classId') || null);
        setTeacherFilter(q.get('teacherId') || null);
    }, [location.search]);
    useEffect(() => {
        const q = new URLSearchParams();
        if (classFilter && classes.some(c => c.class_id === classFilter)) q.set('classId', classFilter);
        if (teacherFilter) q.set('teacherId', teacherFilter);
        const next = q.toString() ? `/schedule?${q.toString()}` : '/schedule';
        if (`${location.pathname}${location.search}` !== next) history.replace(next);
        if (classFilter && classes.some(c => c.class_id === classFilter) && add.open && !add.classId) {
            setAdd(a => ({ ...a, classId: classFilter }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classFilter, teacherFilter, classes.length]);

    const activeClasses = useMemo(() => classes.filter(c => !c.archived), [classes]);
    const visibleClasses = useMemo(() => {
        return classes.filter(c => !classFilter || c.class_id === classFilter);
    }, [classes, classFilter]);

    const teacherOptions = useMemo(() => {
        const seen = new Map();
        for (const cls of classes) {
            for (const t of [cls.class_teacher, ...(cls.assigned_teachers || [])].filter(Boolean)) {
                const id = t.student_id || t._id;
                if (!seen.has(id)) seen.set(id, t.name || t.student_id || t._id);
            }
        }
        return [...seen.entries()].map(([value, label]) => ({ value, label }));
    }, [classes]);

    const { byDay, agendaByDay, rangeStart, rangeEnd } = useMemo(() => {
        const visible = visibleClasses.filter(c => {
            if (!teacherFilter) return true;
            const ids = [
                c.class_teacher?.student_id, c.class_teacher?._id,
                ...(c.assigned_teachers || []).flatMap(t => [t.student_id, t._id]),
            ];
            return ids.includes(teacherFilter);
        });
        const byDay = {};
        WEEKDAYS.forEach(({ iso }) => { byDay[iso] = []; });
        let min = Infinity, max = -Infinity;
        for (const cls of visible) {
            const slots = cls.schedule_slots || [];
            for (let slotIdx = 0; slotIdx < slots.length; slotIdx++) {
                const slot = slots[slotIdx];
                const start = toMin(slot.start);
                if (start == null || !byDay[slot.weekday]) continue;
                const end = start + (Number(slot.duration_min) || 0);
                byDay[slot.weekday].push({ cls, slot, slotIdx, start, end });
                min = Math.min(min, start);
                max = Math.max(max, end);
            }
        }
        // Overlay 1:1 makeup sessions (current week) as read-only blocks.
        for (const r of oneOnOnes) {
            if (teacherFilter && ![r.teacher_ref?._id, r.teacher_ref?.student_id].includes(teacherFilter)) continue;
            const start = toMin(r.time_in);
            if (start == null) continue;
            const iso = ((dayjs(r.date).day() + 6) % 7) + 1; // dayjs 0=Sun → iso 1=Mon..7=Sun
            if (!byDay[iso]) continue;
            const end = TIME_RE.test(r.time_out || '') ? toMin(r.time_out) : start + 60;
            byDay[iso].push({ oneOnOne: r, start, end });
            min = Math.min(min, start);
            max = Math.max(max, end);
        }
        // Overlay the REAL projected sessions for this week (solid). Joined to the loaded class for
        // teacher (filter/colour) + duration (the session stores only its start). Archived → skipped.
        const clsById = new Map(visibleClasses.map(c => [c.class_id, c]));
        for (const s of realSessions) {
            const cls = clsById.get(s.class_ref?.class_id);
            if (!cls) continue;
            if (teacherFilter) {
                const ids = [
                    cls.class_teacher?.student_id, cls.class_teacher?._id,
                    ...(cls.assigned_teachers || []).flatMap(t => [t.student_id, t._id]),
                ];
                if (!ids.includes(teacherFilter)) continue;
            }
            const dt = dayjs(s.date);
            const iso = ((dt.day() + 6) % 7) + 1;
            if (!byDay[iso]) continue;
            const hhmm = dt.format('HH:mm');
            const start = toMin(hhmm);
            if (start == null) continue;
            const slot = (cls.schedule_slots || []).find(sl => sl.weekday === iso && sl.start === hhmm);
            const end = start + (Number(s.duration_min) || (slot && Number(slot.duration_min)) || 90);
            byDay[iso].push({ real: s, cls, start, end });
            min = Math.min(min, start);
            max = Math.max(max, end);
        }
        // Dedup: where a real session occupies a template slot this week, drop the template
        // block so the slot shows ONE block only (the real session). The template-mẫu shadow
        // is then expressed by the real block's own style once it's held/cancelled (below).
        for (const iso of Object.keys(byDay)) {
            const realKeys = new Set();
            for (const blk of byDay[iso]) {
                if (blk.real) realKeys.add(`${blk.cls.class_id}|${blk.start}`);
            }
            byDay[iso] = byDay[iso].filter(blk =>
                !(blk.slot && !blk.real && realKeys.has(`${blk.cls.class_id}|${blk.start}`)));
        }
        const agendaByDay = {};
        Object.keys(byDay).forEach(iso => {
            const arr = byDay[iso];
            arr.sort((a, b) => a.start - b.start || a.end - b.end);
            agendaByDay[iso] = arr.slice();
            byDay[iso] = buildDayLayout(arr);
        });
        const rangeStart = Number.isFinite(min) ? Math.floor(min / 60) * 60 : DEFAULT_RANGE[0];
        const rangeEnd = Number.isFinite(max) ? Math.ceil(max / 60) * 60 : DEFAULT_RANGE[1];
        return { byDay, agendaByDay, rangeStart, rangeEnd };
    }, [visibleClasses, teacherFilter, oneOnOnes, realSessions]);

    // Extra fixed pixels below the last hour so the bottom row/label isn't flush against the edge.
    const gridHeight = (rangeEnd - rangeStart) * PX_PER_MIN + 24;
    const hourMarks = [];
    for (let m = rangeStart; m <= rangeEnd; m += 60) hourMarks.push(m);
    const focusedDay = WEEKDAYS.find(w => w.iso === focusIso) || WEEKDAYS[0];
    const focusedItems = agendaByDay[focusIso] || [];
    const weekStats = useMemo(() => {
        const all = Object.values(agendaByDay).flat();
        const attention = all.filter(b => b.real?.cancelled || (b.real?.missed && !b.real?.held));
        const byStart = new Map();
        for (const b of all) byStart.set(b.start, (byStart.get(b.start) || 0) + 1);
        let busiest = null;
        for (const [start, count] of byStart.entries()) {
            if (!busiest || count > busiest.count) busiest = { start, count };
        }
        return {
            total: all.length,
            oneOnOne: all.filter(b => b.oneOnOne).length,
            attention: attention.length,
            busiest: busiest ? fmtMin(busiest.start) : '—',
        };
    }, [agendaByDay]);
    const nowMin = dayjs().hour() * 60 + dayjs().minute();
    const showNowLine = weekOffset === 0 && nowMin >= rangeStart && nowMin <= rangeEnd;

    // Persist a class's full slot list, then refresh from server.
    async function putSlots(cls, slots) {
        setSaving(true);
        try {
            const res = await fetchWrapper.put(
                `/api/manager/classes/${cls.class_id}/info`,
                'application/json',
                JSON.stringify({ schedule_slots: slots })
            );
            const json = await res.json();
            if (json.status === 'Success') { await load(); return true; }
            if (res.status === 409 && json.message?.code === 'SCHEDULE_CONFLICT') {
                const lines = (json.message.conflicts || []).slice(0, 5).map(c =>
                    `${c.class_name || c.class_id} ${c.weekday} ${c.time}${c.other_class_name ? ` / ${c.other_class_name} ${c.other_time}` : ''}`
                );
                Modal.confirm({
                    title: 'Trùng lịch',
                    content: <div>{lines.map((l, i) => <div key={i}>{l}</div>)}<p style={{ marginTop: 8 }}>Vẫn lưu lịch này?</p></div>,
                    okText: 'Vẫn lưu',
                    cancelText: 'Sửa lại',
                    onOk: async () => {
                        await fetchWrapper.put(
                            `/api/manager/classes/${cls.class_id}/info`,
                            'application/json',
                            JSON.stringify({ schedule_slots: slots, allow_conflicts: true })
                        );
                        await load();
                    },
                });
                return false;
            }
            message.error(json.message || 'Có lỗi xảy ra');
        } catch (e) { message.error('Có lỗi xảy ra'); }
        finally { setSaving(false); }
        return false;
    }

    // Click behavior shared by lane blocks, strips, and popover rows.
    function openBlock(blk) {
        if (blk.oneOnOne) { history.push('/one-on-one'); return; }
        if (blk.real) { setCancelReason(''); setSession({ open: true, s: blk.real, cls: blk.cls }); return; }
        if (readOnly || blk.cls?.archived) { history.push(`/${classSlug(blk.cls)}/dashboard`); return; }
        setEdit({ open: true, cls: blk.cls, slotIdx: blk.slotIdx, weekday: blk.slot.weekday, start: blk.slot.start, duration: blk.slot.duration_min });
    }

    // Click empty space in a day column → prefill add modal with that day + clicked time.
    function onColumnClick(iso, e) {
        if (e.target !== e.currentTarget) return; // ignore clicks that land on a block
        const offsetY = e.nativeEvent.offsetY;
        let min = rangeStart + offsetY / PX_PER_MIN;
        min = Math.round(min / 15) * 15;
        min = Math.max(rangeStart, Math.min(min, rangeEnd - 15));
        setAdd({ ...blankAdd, open: true, weekday: iso, start: fmtMin(min) });
    }

    async function saveAdd() {
        if (!add.classId) { message.warning('Chọn lớp'); return; }
        if (!TIME_RE.test(add.start)) { message.warning('Giờ không hợp lệ (HH:MM)'); return; }
        const cls = activeClasses.find(c => c.class_id === add.classId);
        if (!cls || cls.archived || readOnly) return;

        if (add.mode === 'once') {
            const lessonName = add.lessonName.trim();
            if (!lessonName) { message.warning('Nhập tên buổi học'); return; }
            setSaving(true);
            try {
                const [hh, mm] = add.start.split(':').map(Number);
                const date = monday.add(add.weekday - 1, 'day').hour(hh).minute(mm).second(0).toISOString();
                const res = await attendanceActions.createSession(cls.class_id, lessonName, date, add.description.trim(), add.duration);
                if (res?.status === 'Success') {
                    message.success('Đã thêm buổi'); setAdd(blankAdd); await loadReal(monday);
                } else {
                    message.error(typeof res?.message === 'string' ? res.message : 'Có lỗi xảy ra');
                }
            } finally { setSaving(false); }
            return;
        }

        const slots = [...(cls.schedule_slots || []), { weekday: add.weekday, start: add.start, duration_min: add.duration }];
        if (await putSlots(cls, slots)) { message.success('Đã thêm buổi'); setAdd(blankAdd); }
    }

    async function saveEdit() {
        if (!TIME_RE.test(edit.start)) { message.warning('Giờ không hợp lệ (HH:MM)'); return; }
        const slots = (edit.cls.schedule_slots || []).map((s, i) =>
            i === edit.slotIdx ? { weekday: edit.weekday, start: edit.start, duration_min: edit.duration } : { weekday: s.weekday, start: s.start, duration_min: s.duration_min });
        if (await putSlots(edit.cls, slots)) { message.success('Đã lưu'); setEdit(blankEdit); }
    }

    async function deleteSlot() {
        const slots = (edit.cls.schedule_slots || [])
            .filter((_, i) => i !== edit.slotIdx)
            .map(s => ({ weekday: s.weekday, start: s.start, duration_min: s.duration_min }));
        if (await putSlots(edit.cls, slots)) { message.success('Đã xoá buổi'); setEdit(blankEdit); }
    }

    // Cancel a real session from the calendar — same endpoint AttendanceTable uses, so
    // KPI/lesson-quota/schedule-projection all pick it up consistently.
    async function cancelRealSession() {
        const reason = cancelReason.trim();
        if (!reason) { message.warning('Vui lòng nhập lý do hủy'); return; }
        setSaving(true);
        try {
            const res = await attendanceActions.cancelSession(session.cls.class_id, session.s._id, reason);
            if (res?.status === 'Success') {
                message.success('Đã hủy buổi học');
                setSession(blankSession); setCancelReason('');
                await loadReal(monday);
            } else {
                message.error(typeof res?.message === 'string' ? res.message : 'Hủy buổi học thất bại');
            }
        } finally { setSaving(false); }
    }

    async function uncancelRealSession() {
        setSaving(true);
        try {
            const res = await attendanceActions.uncancelSession(session.cls.class_id, session.s._id);
            if (res?.status === 'Success') {
                message.success('Đã khôi phục buổi học');
                setSession(blankSession);
                await loadReal(monday);
            } else {
                message.error('Khôi phục thất bại');
            }
        } finally { setSaving(false); }
    }

    return (
        <div className={'cal-page soft-in' + (isMobile ? ' cal-page-mobile' : '')}>
            <div className="cal-head">
                <div className="cal-heading">
                    <h1 className="cal-title">Lịch dạy trong tuần</h1>
                    <div className="cal-sub">
                        {monday.format('DD/MM')} – {monday.add(6, 'day').format('DD/MM')}
                        {weekOffset === 0 ? ' · Tuần này' : ''}
                    </div>
                </div>
                <div className="cal-tools">
                    <Select
                        allowClear
                        className="cal-teacher-select"
                        placeholder="Lọc theo lớp"
                        value={classFilter}
                        onChange={setClassFilter}
                        options={classes.map(c => ({ value: c.class_id, label: `${c.class_name}${c.archived ? ' (lưu trữ)' : ''}` }))}
                        showSearch
                        optionFilterProp="label"
                    />
                    <Select
                        allowClear
                        className="cal-teacher-select"
                        placeholder="Lọc theo giáo viên"
                        value={teacherFilter}
                        onChange={setTeacherFilter}
                        options={teacherOptions}
                        showSearch
                        optionFilterProp="label"
                    />
                    <div className="seg-week">
                        <button type="button" onClick={() => setWeekOffset(o => o - 1)} aria-label="Tuần trước"><LeftOutlined /></button>
                        <button type="button" className={'seg-mid' + (weekOffset === 0 ? ' on' : '')} onClick={() => setWeekOffset(0)}>Tuần này</button>
                        <button type="button" onClick={() => setWeekOffset(o => o + 1)} aria-label="Tuần sau"><RightOutlined /></button>
                    </div>
                    {canManageSchedule &&
                        <Button icon={<ReloadOutlined />} loading={weekPlanLoading} onClick={openWeekPlan}>Tạo lại lịch</Button>}
                </div>
            </div>

            <div className="cal-stats">
                <div className="cal-stat">
                    <CalendarOutlined />
                    <span><strong>{weekStats.total}</strong><small>buổi trong tuần</small></span>
                </div>
                <div className="cal-stat">
                    <ClockCircleOutlined />
                    <span><strong>{focusedItems.length}</strong><small>ngày đang chọn</small></span>
                </div>
                <div className="cal-stat">
                    <CalendarOutlined />
                    <span><strong>{weekStats.attention}</strong><small>cần chú ý</small></span>
                </div>
                <div className="cal-stat">
                    <TeamOutlined />
                    <span><strong>{weekStats.oneOnOne}</strong><small>buổi 1:1</small></span>
                </div>
                <div className="cal-stat">
                    <ClockCircleOutlined />
                    <span><strong>{weekStats.busiest}</strong><small>khung giờ đông nhất</small></span>
                </div>
            </div>

            <div className="cal-legend">
                <span className="cal-key"><span className="cal-swatch cal-sw-real" />Buổi thực tế</span>
                <span className="cal-key"><span className="cal-swatch cal-sw-tpl" />Lịch mẫu</span>
                <span className="cal-key"><span className="cal-swatch cal-sw-one" />1:1</span>
                <span className="cal-key"><span className="cal-badge cal-held">Đã học</span></span>
                <span className="cal-key"><span className="cal-badge cal-cancel">Đã hủy</span></span>
                <span className="cal-key"><span className="cal-badge cal-miss">Bỏ lỡ</span></span>
                <span className="cal-hint">{readOnly ? 'Chế độ xem lịch' : 'Bấm ô trống để thêm · bấm buổi để sửa'}</span>
            </div>

            <div className="cal-day-tabs">
                {WEEKDAYS.map(({ iso, label }) => (
                    <button
                        type="button"
                        key={iso}
                        className={(focusIso === iso ? 'on ' : '') + (isToday(iso) ? 'today' : '')}
                        onClick={() => setFocusIso(iso)}
                    >
                        <span>{label.replace('Thứ ', 'T')}</span>
                        <strong>{monday.add(iso - 1, 'day').format('DD/MM')}</strong>
                    </button>
                ))}
            </div>

            <div className="cal-workspace">
                <section className="cal-board-wrap">
                    <div className="cal-grid soft-in-scale">
                        <div className="cal-time-gutter" style={{ height: gridHeight + HEADER_H }}>
                            <div className="cal-time-top" />
                            {hourMarks.map(m => (
                                <div key={m} className="cal-time-label" style={{ top: HEADER_H + (m - rangeStart) * PX_PER_MIN }}>
                                    {fmtMin(m)}
                                </div>
                            ))}
                        </div>
                        {WEEKDAYS.map(({ iso, label }) => (
                            <div
                                key={iso}
                                className={(isToday(iso) ? 'today ' : '') + (focusIso === iso ? 'focused ' : '') + 'cal-day-column'}
                            >
                                <button type="button" className="cal-day-head" onClick={() => setFocusIso(iso)}>
                                    <span className="cal-day-row">
                                        <span className="cal-day-name">{label}</span>
                                        <span className="cal-day-count">{(agendaByDay[iso] || []).length} buổi</span>
                                    </span>
                                    <span className="cal-day-date">{monday.add(iso - 1, 'day').format('DD/MM')}</span>
                                </button>
                                <div
                                    className="cal-day-body"
                                    style={{ height: gridHeight }}
                                    onClick={(e) => !readOnly && onColumnClick(iso, e)}
                                >
                                    {hourMarks.map(m => (
                                        <div key={m} className="cal-hour-line" style={{ top: (m - rangeStart) * PX_PER_MIN }} />
                                    ))}
                                    {showNowLine && isToday(iso) &&
                                        <div className="cal-now-line" style={{ top: (nowMin - rangeStart) * PX_PER_MIN }} />}
                                    {byDay[iso].map((blk, i) => (
                                        blk.cluster
                                            ? <ClusterBlock key={i} blk={blk} rangeStart={rangeStart} onOpen={() => setFocusIso(iso)} />
                                            : <EventBlock key={i} blk={blk} rangeStart={rangeStart} openBlock={openBlock} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <aside className="cal-focus-panel">
                    <div className="cal-focus-head">
                        <button
                            type="button"
                            onClick={() => setFocusIso(iso => Math.max(1, iso - 1))}
                            disabled={focusIso === 1}
                            aria-label="Ngày trước"
                        ><LeftOutlined /></button>
                        <div>
                            <div className="cal-focus-title">{focusedDay.label}</div>
                            <div className="cal-focus-date">{monday.add(focusedDay.iso - 1, 'day').format('DD/MM/YYYY')}</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFocusIso(iso => Math.min(7, iso + 1))}
                            disabled={focusIso === 7}
                            aria-label="Ngày sau"
                        ><RightOutlined /></button>
                    </div>
                    <div className="cal-focus-count">
                        <strong>{focusedItems.length}</strong>
                        <span>buổi cần quản lý trong ngày này</span>
                    </div>
                    <div className="cal-agenda-list">
                        {focusedItems.length
                            ? focusedItems.map((blk, i) => <AgendaItem key={i} blk={blk} openBlock={openBlock} />)
                            : <button
                                type="button"
                                className="cal-agenda-empty"
                                onClick={() => !readOnly && setAdd({ ...blankAdd, open: true, weekday: focusIso })}
                            >
                                {readOnly ? 'Chưa có buổi nào trong ngày này.' : 'Chưa có buổi nào. Bấm để thêm buổi cho ngày này.'}
                            </button>}
                    </div>
                </aside>
            </div>

            {/* Add lesson */}
            {canManageSchedule && <Modal
                title="Thêm buổi học"
                visible={add.open}
                onCancel={() => setAdd(blankAdd)}
                onOk={saveAdd}
                confirmLoading={saving}
                okText="Thêm" cancelText="Hủy"
            >
                <SlotFields
                    classOptions={activeClasses.map(c => ({ value: c.class_id, label: c.class_name }))}
                    classId={add.classId} onClass={v => setAdd(a => ({ ...a, classId: v }))}
                    mode={add.mode} onMode={v => setAdd(a => ({ ...a, mode: v }))}
                    lessonName={add.lessonName} onLessonName={v => setAdd(a => ({ ...a, lessonName: v }))}
                    description={add.description} onDescription={v => setAdd(a => ({ ...a, description: v }))}
                    weekday={add.weekday} onWeekday={v => setAdd(a => ({ ...a, weekday: v }))}
                    start={add.start} onStart={v => setAdd(a => ({ ...a, start: v }))}
                    duration={add.duration} onDuration={v => setAdd(a => ({ ...a, duration: v }))}
                />
            </Modal>}

            {/* Edit / delete lesson */}
            {canManageSchedule && <Modal
                title={`Sửa buổi: ${edit.cls?.class_name || ''}`}
                visible={edit.open}
                onCancel={() => setEdit(blankEdit)}
                confirmLoading={saving}
                footer={[
                    <Button key="open" onClick={() => edit.cls && history.push(`/${classSlug(edit.cls)}/`)}>Mở lớp</Button>,
                    <Button key="del" danger onClick={deleteSlot}>Xoá buổi</Button>,
                    <Button key="cancel" onClick={() => setEdit(blankEdit)}>Hủy</Button>,
                    <Button key="ok" type="primary" loading={saving} onClick={saveEdit}>Lưu</Button>,
                ]}
            >
                <SlotFields
                    weekday={edit.weekday} onWeekday={v => setEdit(e => ({ ...e, weekday: v }))}
                    start={edit.start} onStart={v => setEdit(e => ({ ...e, start: v }))}
                    duration={edit.duration} onDuration={v => setEdit(e => ({ ...e, duration: v }))}
                />
            </Modal>}

            {/* Real session: open class, or cancel/uncancel (huỷ buổi) */}
            <Modal
                title={session.s ? `${session.cls.class_name} • ${session.s.lesson_name}` : ''}
                visible={session.open}
                onCancel={() => { setSession(blankSession); setCancelReason(''); }}
                confirmLoading={saving}
                footer={[
                    <Button key="open" onClick={() => session.cls && history.push(`/${classSlug(session.cls)}/attendance`)}>Mở lớp</Button>,
                    canManageSchedule && session.s?.cancelled
                        ? <Button key="uncancel" danger loading={saving} onClick={uncancelRealSession}>Bỏ hủy</Button>
                        : canManageSchedule ? <Button key="cancel" danger loading={saving} onClick={cancelRealSession}>Hủy buổi</Button> : null,
                    <Button key="close" onClick={() => { setSession(blankSession); setCancelReason(''); }}>Đóng</Button>,
                ]}
            >
                {session.s?.cancelled ? (
                    <p>Buổi này đã bị hủy{session.s.cancel_reason ? `: ${session.s.cancel_reason}` : ''}.</p>
                ) : readOnly ? (
                    <p>Bạn đang xem buổi học này ở chế độ chỉ xem.</p>
                ) : (
                    <Input.TextArea
                        placeholder="Lý do hủy buổi (bắt buộc)"
                        value={cancelReason}
                        onChange={e => setCancelReason(e.target.value)}
                        rows={3}
                    />
                )}
            </Modal>

            {/* Preview + confirm for "Tạo lại lịch" (bulk-fill the displayed week from schedule_slots) */}
            <Modal
                title={`Tạo lại lịch — ${monday.format('DD/MM')} – ${monday.add(6, 'day').format('DD/MM')}`}
                visible={weekPlan !== null}
                onCancel={() => setWeekPlan(null)}
                confirmLoading={weekPlanLoading}
                footer={[
                    <Button key="close" onClick={() => setWeekPlan(null)}>Đóng</Button>,
                    <Button key="confirm" type="primary" loading={weekPlanLoading}
                        disabled={!weekPlan?.toCreate?.length} onClick={confirmWeekPlan}>
                        Tạo {weekPlan?.toCreate?.length || 0} buổi
                    </Button>,
                ]}
            >
                {weekPlan?.toCreate?.length
                    ? <>
                        <p>Sẽ tạo {weekPlan.toCreate.length} buổi:</p>
                        <ul style={{ maxHeight: 200, overflowY: 'auto', paddingLeft: 20 }}>
                            {weekPlan.toCreate.map((it, i) => (
                                <li key={i}>{it.class_name} — {dayjs(it.date).format('ddd DD/MM HH:mm')}</li>
                            ))}
                        </ul>
                    </>
                    : <p>Không có buổi nào cần tạo — mọi slot trong tuần này đã có buổi.</p>}
                {weekPlan?.toSkip?.length > 0 &&
                    <p style={{ color: 'var(--ink-4)', marginTop: 8 }}>
                        Bỏ qua {weekPlan.toSkip.length} buổi đã có sẵn.
                    </p>}
            </Modal>
        </div>
    );
}

// Shared form rows for add/edit. classOptions present → show class picker + one-time/weekly toggle (add mode).
function SlotFields({ classOptions, classId, onClass, mode, onMode, lessonName, onLessonName, description, onDescription, weekday, onWeekday, start, onStart, duration, onDuration }) {
    const once = classOptions && mode === 'once';
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {classOptions &&
                <Select
                    placeholder="Chọn lớp" value={classId} onChange={onClass}
                    options={classOptions} showSearch optionFilterProp="label"
                />}
            {classOptions &&
                <Select
                    value={mode} onChange={onMode}
                    options={[
                        { value: 'weekly', label: 'Lặp lại hàng tuần' },
                        { value: 'once', label: 'Một lần (chỉ tuần này)' },
                    ]}
                />}
            {once &&
                <Input
                    placeholder="Tên buổi học" value={lessonName}
                    onChange={e => onLessonName(e.target.value)}
                />}
            {once &&
                <Input.TextArea
                    placeholder="Nội dung buổi học (tùy chọn)" value={description} rows={3}
                    onChange={e => onDescription(e.target.value)}
                />}
            <Select
                value={weekday} onChange={onWeekday}
                options={WEEKDAYS.map(w => ({ value: w.iso, label: w.label }))}
            />
            <input
                type="time" value={start} onChange={e => onStart(e.target.value)}
                style={{ height: 32, padding: '0 8px', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)' }}
            />
            <span>
                <InputNumber
                    min={1} max={600} step={15} value={duration} onChange={onDuration}
                    style={{ width: 90 }}
                />
                <span style={{ marginLeft: 8 }}>phút</span>
            </span>
        </div>
    );
}
